import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { pickBy, startCase, head, capitalize, get } from 'lodash';

import Pagination from 'partial/components/Pagination';
import SearchBar from 'partial/components/input/SearchBar';
import ThumbIcon from 'partial/components/ThumbIcon';
import { Circle, Plus } from '@phosphor-icons/react';
import classNames from 'classnames';
import EmptyState from 'partial/components/EmptyState';

import List from 'partial/components/List';
import { usePersistState } from 'helper';
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { useModuleContext } from 'partial/wrappers/ModuleWrapper';
import Button from 'partial/components/Button';
import { useCreateModal } from 'partial/components/Modal';
import VerificationDetails from './components/VerificationDetails';
import { useSubscribersList } from './hooks';
import AddUpdateOrganization from './modals/AddEditOrganization';

const INITIAL_STATE = {
  keyword: '',
};

export function ForVerification() {
  const { setSubLabel } = useModuleContext();
  const params = useParams();
  const id = get(params, '*');

  const [filter, setFilter] = useState(INITIAL_STATE);
  const [deleted, setDeleted] = useState(false);

  const [page, setPage] = useState({ page: '' });

  const payload = useMemo(
    () =>
      pickBy({
        ...filter,
        ...page,
        status: 'PENDING',
        per_page: 15,
      }),
    [filter, page],
  );
  const [isLoading, list, meta] = useSubscribersList(payload);

  const [selected, setSelected] = usePersistState(
    'verification-request-id',
    '',
    {
      keepOnUnmount: false,
    },
  );

  const navigate = useNavigate();

  const onSubSelect = (item) => {
    setDeleted(false);
    setSelected(item?.uuid);
    navigate(`${item?.uuid}`);
  };

  const createModal = useCreateModal();

  const handleAddOrganization = useCallback(() => {
    createModal({
      content: (onClose) => <AddUpdateOrganization onClose={onClose} />,
    });
  }, [createModal]);

  useEffect(() => {
    if (!id) return;
    setSelected(id);
  }, [id, setSelected]);

  useEffect(() => {
    if ((selected && !isLoading) || deleted || id) return;
    setSelected(head(list)?.uuid);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isLoading, selected, setSelected]);

  useEffect(() => {
    setPage({ page: '' });
  }, [filter]);

  useEffect(() => {
    if (!meta) return;
    setSubLabel(
      meta?.total === 0
        ? ''
        : `Total of ${meta?.total} onboarding item${
            meta?.total === 1 ? '' : 's'
          }`,
    );
    return () => setSubLabel('');
  }, [meta, setSubLabel]);

  const callBack = () => {
    setSelected('');
    setDeleted(true);
    navigate('', { replace: true });
  };

  return (
    <section className="min-h-[calc(100vh_-_5rem)] flex bg-white border border-neutral-200 rounded-md flex-grow overflow-hidden static">
      <div className="border-r border-neutral-200 w-[300px] flex-shrink-0 flex flex-col">
        <div className="p-3 border-b border-neutral-200">
          <SearchBar
            name="q"
            value={filter.q}
            onChange={setFilter}
            inputClassName="!w-full"
            containerClassName="!w-full"
          />
        </div>
        <div className="flex-1 overflow-y-auto">
          <List
            data={list}
            isLoading={isLoading}
            selectedId={selected}
            onSelectRow={onSubSelect}
            containerClassName="!w-full"
            render={(item) => {
              const name = startCase(
                capitalize(
                  `${item?.profile?.first_name} ${item?.profile?.last_name}`,
                ),
              );
              return (
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3 px-4 py-3 w-full min-w-0">
                    <ThumbIcon
                      className="h-9 w-9 rounded-full"
                      alt={name}
                      url={item?.profile?.avatar || ''}
                    />
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{name}</div>
                      <div
                        className={classNames(
                          'text-xs font-semibold',
                          // eslint-disable-next-line no-nested-ternary
                          item?.status === 'Approved'
                            ? 'text-success-700'
                            : item?.status === 'Rejected'
                            ? 'text-danger-700'
                            : 'text-neutral-500',
                        )}
                      >
                        {item?.status}
                      </div>
                    </div>
                  </div>
                  <div
                    className={classNames(
                      'transition',
                      item?.status === 'Pending' && item?.uuid !== selected
                        ? 'opacity-100'
                        : 'opacity-0',
                    )}
                  >
                    <Circle
                      size={8}
                      weight="fill"
                      className="mr-4 text-warning-500"
                    />
                  </div>
                </div>
              );
            }}
            selectedKey="uuid"
            emptyGlyph="records"
            emptyStatement="Nothing is awaiting onboarding."
            emptyGuidance="New agency requests appear here for review."
            emptyAction={
              <Button primary icon={Plus} onClick={handleAddOrganization}>
                Add agency
              </Button>
            }
          />
        </div>
        <div className="p-3 border-t border-neutral-200 bg-neutral-50 space-y-3">
          <Button
            primary
            icon={Plus}
            className="w-full"
            onClick={handleAddOrganization}
          >
            Add agency
          </Button>
          <div className="flex items-center justify-between">
            <p className="text-xs text-neutral-500 tabular-nums">
              {list?.length || 0} of {meta?.total || 0}
            </p>
            <Pagination value={meta} onChange={setPage} withPage={false} />
          </div>
        </div>
      </div>
      <Routes>
        <Route
          path=":id/*"
          element={<VerificationDetails callBack={callBack} />}
        />
        {list?.length > 0 && !deleted && (
          <Route
            index
            element={<Navigate replace to={`${head(list)?.uuid}`} />}
          />
        )}
        {/*
          Audit 6.7: the rail owns the empty message and the create action.
          The detail pane stays blank with a single line and never repeats it.
        */}
        <Route
          path="*"
          element={
            <div className="flex-1">
              <EmptyState
                variant="detail"
                statement="Select a request to review it."
              />
            </div>
          }
        />
      </Routes>
    </section>
  );
}

export default ForVerification;
